/**
 * Asks for the destination clusters of a departure, but only once a departure is chosen: the server rejects an empty
 * one, and the departure is empty on purpose when the booking's own departure is no longer offered.
 *
 * @param {string|number|null|undefined} departureParkingAreaId value of the departure select
 * @param {(departureParkingAreaId: string) => *} sendRequest starts the request, returns its handle
 * @returns {*|null} the handle, or null when no request was sent
 */
export function requestClustersForDeparture(departureParkingAreaId, sendRequest) {
    const departureId = departureParkingAreaId == null ? "" : String(departureParkingAreaId);
    if (departureId === "") {
        return null;
    }
    return sendRequest(departureId);
}

/**
 * Destinations a clusters response allows. On the form's first load the booking's own destination stays allowed even
 * outside the clusters, so an edit keeps it selected; after a departure change only the clusters count.
 *
 * @param {Array<string|number>} clusters ids of the response
 * @param {(string|number|null|undefined)} keptDestinationId the booking's destination on the first load, else empty
 * @returns {Set<string>}
 */
export function allowedDestinationIds(clusters, keptDestinationId) {
    const ids = new Set(clusters.map(String));
    if (keptDestinationId != null && String(keptDestinationId) !== "") {
        ids.add(String(keptDestinationId));
    }
    return ids;
}

/**
 * Hands a clusters response to the destination selector, the same way on the web and admin forms. On the form's first
 * load the booking's own destination stays allowed and the current choice is kept; after a departure change only the
 * clusters count and the departure is preferred as destination.
 *
 * @param {{setAllowed: function(Set<string>, string): void, selectedId: function(): string}} destination the
 *        destination selector's controller
 * @param {Array<string|number>} clusters ids of the response
 * @param {{onChange: boolean, departureId: string, requestedDestinationId: string}} request onChange: the request
 *        followed a departure change; departureId: the departure it was sent for; requestedDestinationId: the
 *        booking's destination
 */
export function applyClustersResponse(destination, clusters, {onChange, departureId, requestedDestinationId}) {
    if (onChange) {
        destination.setAllowed(allowedDestinationIds(clusters, ""), departureId);
        return;
    }
    destination.setAllowed(allowedDestinationIds(clusters, requestedDestinationId), destination.selectedId());
}

/**
 * Keeps only the latest clusters request: starting one aborts the one still pending, so the response for an older
 * departure can never be applied after the one for a newer departure. An aborted request ends in its fail callback
 * with status "abort", which the forms already ignore.
 *
 * @param {function(*): void} abortRequest aborts a request handle; harmless on a request that already finished
 * @returns {function(function(): *): *} runs sendRequest after aborting the previous request, returns the new handle
 */
export function latestRequestOnly(abortRequest) {
    let pending = null;
    return (sendRequest) => {
        if (pending !== null) {
            abortRequest(pending);
        }
        pending = sendRequest();
        return pending;
    };
}
